import React, { useMemo, useState } from 'react';
import {
  useDroppable,
} from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { Button } from './ui/button';
import { Input } from './ui/input';
import EquipmentSortableCard from './EquipmentSortableCard';
import GroupStatusRollup from './GroupStatusRollup';

/**
 * One section on the Location Equipment page.
 *
 * - `group === null` renders the "Ungrouped" bucket.
 * - Otherwise renders a named group with rename/delete controls.
 *
 * Uses dnd-kit's useDroppable so cards can be dropped INTO an empty section
 * (SortableContext alone can't accept drops onto an empty list).
 */
const GroupSection = ({
  group,
  machinery,
  viewMode,
  isAdmin,
  onRename,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  dndDisabled,
}) => {
  const droppableId = group ? `group:${group.id}` : 'group:__ungrouped__';
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });

  const [collapsed, setCollapsed] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState(group?.name || '');

  const startRename = () => {
    setNameDraft(group?.name || '');
    setRenaming(true);
  };
  const commitRename = () => {
    const next = nameDraft.trim();
    if (next && next !== group.name) onRename(group.id, next);
    setRenaming(false);
  };

  const items = machinery.map((m) => m.id);

  return (
    <section
      className={`bg-white rounded-lg shadow p-4 transition ${
        isOver ? 'ring-2 ring-slate-900' : ''
      }`}
      data-testid={group ? `group-section-${group.id}` : 'group-section-ungrouped'}
    >
      <header className="flex flex-wrap items-center gap-3 mb-3">
        <button
          type="button"
          onClick={() => setCollapsed((c) => !c)}
          className="text-slate-500 hover:text-slate-900 text-lg leading-none w-5"
          aria-label={collapsed ? 'Expand group' : 'Collapse group'}
          data-testid={group ? `group-toggle-${group.id}` : 'group-toggle-ungrouped'}
        >
          {collapsed ? '▸' : '▾'}
        </button>

        {group ? (
          renaming ? (
            <Input
              autoFocus
              value={nameDraft}
              onChange={(e) => setNameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') setRenaming(false);
              }}
              className="max-w-xs h-8"
              data-testid={`group-rename-input-${group.id}`}
            />
          ) : (
            <h2 className="text-lg font-semibold text-slate-900" data-testid={`group-name-${group.id}`}>
              {group.name}
            </h2>
          )
        ) : (
          <h2 className="text-lg font-semibold text-slate-500 italic">Ungrouped</h2>
        )}

        <span className="text-xs text-slate-500" data-testid={group ? `group-count-${group.id}` : 'group-count-ungrouped'}>
          {machinery.length} item{machinery.length === 1 ? '' : 's'}
        </span>

        <div className="flex-1 min-w-0" />

        <GroupStatusRollup machinery={machinery} />

        {group && isAdmin && !renaming && (
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveUp?.(group)}
              disabled={!canMoveUp}
              aria-label="Move group up"
              title="Move group up"
              data-testid={`group-move-up-btn-${group.id}`}
              className="h-7 px-2 text-xs"
            >
              ▲
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onMoveDown?.(group)}
              disabled={!canMoveDown}
              aria-label="Move group down"
              title="Move group down"
              data-testid={`group-move-down-btn-${group.id}`}
              className="h-7 px-2 text-xs"
            >
              ▼
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={startRename}
              data-testid={`group-rename-btn-${group.id}`}
              className="h-7 px-2 text-xs"
            >
              Rename
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDelete(group)}
              data-testid={`group-delete-btn-${group.id}`}
              className="h-7 px-2 text-xs text-red-700 hover:bg-red-50"
            >
              Delete
            </Button>
          </div>
        )}
      </header>

      {!collapsed && (
        <div
          ref={setNodeRef}
          className={`min-h-[64px] ${
            machinery.length === 0
              ? 'border-2 border-dashed border-slate-200 rounded-md text-sm text-slate-400 italic flex items-center justify-center py-6'
              : ''
          }`}
        >
          <SortableContext items={items} strategy={rectSortingStrategy}>
            {machinery.length === 0 ? (
              <span>Drop equipment here</span>
            ) : viewMode === 'chip' ? (
              <div className="flex flex-wrap gap-2">
                {machinery.map((m) => (
                  <EquipmentSortableCard
                    key={m.id}
                    machinery={m}
                    viewMode="chip"
                    isAdmin={isAdmin}
                    dndDisabled={dndDisabled}
                  />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {machinery.map((m) => (
                  <EquipmentSortableCard
                    key={m.id}
                    machinery={m}
                    viewMode="card"
                    isAdmin={isAdmin}
                    dndDisabled={dndDisabled}
                  />
                ))}
              </div>
            )}
          </SortableContext>
        </div>
      )}
    </section>
  );
};

export default GroupSection;
